import React, { useEffect, useRef, useState } from 'react';
import { authService } from '../services/auth';
import { GRAD, textoDegradado } from '../lib/brand';

/*
  Pantalla/bloque de «escribe el código que te mandamos al correo».

  Vive en dos sitios con el mismo componente para que el paso se vea y se
  comporte igual en ambos:

    · en el registro del beneficio gratis (Auth.jsx), como paso entre crear la
      cuenta y desbloquear — ahí el primer código ya salió solo con el alta,
      así que `autoEnviar` va en false;
    · en el catálogo (Benefits.jsx), como modal, para las cuentas que se
      registraron antes de que existiera esta regla — su código de alta nunca
      existió o ya caducó, así que ahí `autoEnviar` va en true.

  El backend limita el reenvío a 1/min; el contador de 60s del botón existe
  para que el usuario no se estrelle contra ese 429 sin explicación.
*/
const VerificarCorreo = ({ correo, autoEnviar = false, onVerificado, onCancelar }) => {
  const [codigo, setCodigo] = useState('');
  const [verificando, setVerificando] = useState(false);
  const [reenviando, setReenviando] = useState(false);
  const [error, setError] = useState(null);
  const [aviso, setAviso] = useState(null);
  const [espera, setEspera] = useState(autoEnviar ? 60 : 0);

  /* El auto-envío debe correr una sola vez aunque el componente re-renderice. */
  const enviadoRef = useRef(false);

  useEffect(() => {
    if (!autoEnviar || enviadoRef.current) return;
    enviadoRef.current = true;
    authService.enviarCodigoVerificacion().catch(() => {
      /* Si falla, el botón de reenviar sigue ahí; no hay que romper el paso. */
    });
  }, [autoEnviar]);

  useEffect(() => {
    if (espera <= 0) return;
    const t = setTimeout(() => setEspera((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [espera]);

  const verificar = async (e) => {
    e?.preventDefault?.();
    if (codigo.length !== 6 || verificando) return;

    setVerificando(true);
    setError(null);
    try {
      await authService.verificarCorreo(codigo);
      onVerificado?.();
    } catch (err) {
      setError(err?.message || 'No se pudo verificar el código. Inténtalo de nuevo.');
      if (err?.data?.code === 'CODIGO_EXPIRADO') setCodigo('');
    } finally {
      setVerificando(false);
    }
  };

  const reenviar = async () => {
    if (espera > 0 || reenviando) return;
    setReenviando(true);
    setError(null);
    setAviso(null);
    try {
      const res = await authService.enviarCodigoVerificacion();
      setAviso(res?.message || 'Te enviamos un código nuevo.');
      setEspera(60);
    } catch (err) {
      setError(
        err?.status === 429
          ? 'Acabamos de enviarte un código. Espera un minuto antes de pedir otro.'
          : (err?.message || 'No pudimos enviar el correo. Inténtalo en un momento.')
      );
    } finally {
      setReenviando(false);
    }
  };

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="text-center pt-2">
        <h2
          className="font-display font-bold text-vd-tinta"
          style={{ fontSize: 'clamp(24px,6vw,32px)', letterSpacing: '-0.02em', lineHeight: 1.15 }}
        >
          Confirma tu{' '}
          <span style={textoDegradado(GRAD.marca)}>correo</span>
        </h2>
        <p className="text-vd-pizarra text-[15px] mt-3" style={{ lineHeight: 1.45 }}>
          Te enviamos un código de 6 dígitos a{' '}
          <span className="font-semibold text-vd-tinta">{correo || 'tu correo'}</span>.
          Revisa también tu carpeta de spam.
        </p>
      </div>

      <form onSubmit={verificar} className="space-y-3">
        <input
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          value={codigo}
          onChange={(e) => setCodigo(e.target.value.replace(/\D/g, '').slice(0, 6))}
          placeholder="______"
          autoFocus
          className="bg-vd-niebla border border-vd-linea text-vd-tinta rounded-full h-14 w-full text-center font-semibold focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-vd-naranja"
          style={{ fontSize: 24, letterSpacing: '0.4em' }}
        />

        {error && (
          <div className="bg-vd-alerta/8 border border-vd-alerta/25 rounded-2xl p-3">
            <p className="text-vd-alerta text-sm text-center">{error}</p>
          </div>
        )}
        {aviso && !error && (
          <div className="bg-vd-exito/8 border border-vd-exito/25 rounded-2xl p-3">
            <p className="text-vd-exito text-sm text-center">{aviso}</p>
          </div>
        )}

        <button
          type="submit"
          disabled={codigo.length !== 6 || verificando}
          className="bg-grad-rojo-rosa text-white rounded-full font-semibold w-full"
          style={{
            height: 56,
            border: 'none',
            fontSize: 16,
            cursor: codigo.length === 6 && !verificando ? 'pointer' : 'not-allowed',
            opacity: codigo.length === 6 && !verificando ? 1 : 0.55,
            boxShadow: '0 12px 28px -10px rgba(245,43,22,0.55)',
          }}
        >
          {verificando ? 'Verificando...' : 'Verificar'}
        </button>
      </form>

      <div className="flex flex-col items-center gap-3 text-sm">
        <button
          type="button"
          onClick={reenviar}
          disabled={espera > 0 || reenviando}
          className="text-vd-pizarra hover:text-vd-tinta transition-colors underline underline-offset-4 decoration-vd-linea disabled:no-underline"
          style={{ opacity: espera > 0 || reenviando ? 0.55 : 1, cursor: espera > 0 || reenviando ? 'default' : 'pointer' }}
        >
          {espera > 0
            ? `Reenviar código (${espera}s)`
            : (reenviando ? 'Enviando...' : '¿No te llegó? Reenviar código')}
        </button>

        {onCancelar && (
          <button
            type="button"
            onClick={onCancelar}
            className="text-vd-pizarra hover:text-vd-tinta transition-colors"
          >
            Ahora no
          </button>
        )}
      </div>
    </div>
  );
};

export default VerificarCorreo;
